import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useUpdateQueryParams } from 'vopay';
import { DEFAULT_ORGANIZATION_SHOW_FILTER, OrganizationShowFilter } from './organizationTree';

const CHART_VIEW = 'chart';

/** What the Overview's View links append to the list pages' paths. */
export const CHART_VIEW_SEARCH = `?view=${CHART_VIEW}`;

type QueryParams = {
  /** `chart` for the chart view; absent for the table. */
  view: string;
  /** The Overview's Show vocabulary: the whole organization, or absent for the page's own branches. */
  show: string;
};

type UseChartViewProps = {
  /** The branches the page's chart draws on its own: the Accounts page's direct accounts, the Partners page's partners. */
  pageShow: OrganizationShowFilter;
};

/**
 * The Accounts and Partners pages' chart view (ID-14095, spec 6.9), in the address beside the page's
 * filters: `view=chart` swaps the table for the chart, and `show` is the chart's checkbox that adds
 * the other kind of branch. Both are written with `history.replace`, as the filters are, so Back
 * leaves the page rather than flipping the view; both go when the table comes back.
 */
export default function useChartView({ pageShow }: UseChartViewProps) {
  const location = useLocation();
  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isChartView = queryParams.get('view') === CHART_VIEW;
  const isShowingWholeOrganization = isChartView && queryParams.get('show') === DEFAULT_ORGANIZATION_SHOW_FILTER;

  return {
    isChartView,
    show: isShowingWholeOrganization ? DEFAULT_ORGANIZATION_SHOW_FILTER : pageShow,
    isShowingWholeOrganization,
    setChartView: (isOn: boolean) => updateQueryParams({ view: isOn ? CHART_VIEW : '', show: '' }),
    setShowingWholeOrganization: (isOn: boolean) =>
      updateQueryParams({ show: isOn ? DEFAULT_ORGANIZATION_SHOW_FILTER : '' }),
  };
}
